import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalculateIcon from "@mui/icons-material/Calculate";
import Footer from "components/Footer";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import Navbar from "components/Navbar";
import { noTokenApi } from "api";
import { addMessage } from "Redux/messageReducer";
import { useDispatch } from "react-redux";
import KpiCard from "./components/KpiCard";
import RoiInputSummaryChips from "./components/RoiInputSummaryChips";
import RoiBars from "./components/RoiBars";
import SliderField from "./components/SliderField";
import {
  calculateRoi,
  formatMoney,
  formatNumber,
  initialRoiState,
  roiWizardSteps,
  sectorLabels,
} from "./roiUtils";
import { RoiState, Sector } from "./types";

export default function RoiPage() {
  const [lang, setLang] = useState("en");
  const [activeStep, setActiveStep] = useState(0);
  const [state, setState] = useState<RoiState>(initialRoiState);
  const dispatch = useDispatch();
  const results = useMemo(() => calculateRoi(state), [state]);

  useEffect(() => {
    (async () => {
      const browserLang = navigator.language.split("-")[0];
      setLang(browserLang);
      const result = await noTokenApi.get(`messages/${browserLang}`);
      document.documentElement.setAttribute("lang", browserLang);
      dispatch(addMessage(result.data.messages));
    })();
  }, [dispatch]);

  const updateState = <K extends keyof RoiState>(key: K, value: RoiState[K]) => {
    setState((current) => ({ ...current, [key]: value }));
  };

  const handleSectorChange = (event: SelectChangeEvent<Sector>) => {
    updateState("sector", event.target.value as Sector);
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f7" }}>
      <Navbar lang={lang} isScrolled />
      <Box
        component="main"
        sx={{
          py: { xs: 8, md: 12 },
          background:
            "linear-gradient(180deg, #f5f5f7 0%, #ffffff 42%, #f8fbfb 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={5}>
              <Chip
                label="Flowenti ROI Wizard"
                sx={{
                  mb: 3,
                  fontWeight: 800,
                  color: "secondary.main",
                  backgroundColor: "rgba(84, 189, 193, 0.14)",
                }}
              />
              <Typography
                variant="h1"
                color="secondary.main"
                sx={{ fontSize: { xs: 34, md: 48 }, lineHeight: 1.08, mb: 3 }}
              >
                Calcola il ritorno dei tuoi processi automatizzati
              </Typography>
              <Typography variant="h5" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Stima in pochi passaggi il risparmio annuale, le ore recuperate e il
                payback period usando benchmark di processo.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: "1px solid rgba(84, 189, 193, 0.28)",
                  boxShadow: "0 20px 60px rgba(30, 53, 95, 0.12)",
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 7 } }}>
                  <Stack spacing={5}>
                    <Box>
                      <Typography variant="h3" color="secondary.main" gutterBottom>
                        ROI Calculator
                      </Typography>
                      <Typography color="text.secondary">
                        Completa il wizard per ottenere una stima immediata.
                      </Typography>
                    </Box>

                    <Stepper activeStep={activeStep} alternativeLabel>
                      {roiWizardSteps.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>

                    <Divider />

                    {activeStep === 0 && (
                      <Stack spacing={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="roi-sector-label">Settore</InputLabel>
                          <Select
                            labelId="roi-sector-label"
                            label="Settore"
                            value={state.sector}
                            onChange={handleSectorChange}
                          >
                            {Object.entries(sectorLabels).map(([value, label]) => (
                              <MenuItem key={value} value={value}>
                                {label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <SliderField
                          label="Dipendenti coinvolti nei processi"
                          min={5}
                          max={500}
                          value={state.employees}
                          unit="persone"
                          onChange={(value) => updateState("employees", value)}
                        />
                        <SliderField
                          label="Processi attivi previsti"
                          min={10}
                          max={1000}
                          step={10}
                          value={state.processes}
                          unit="processi attivi"
                          onChange={(value) => updateState("processes", value)}
                        />
                      </Stack>
                    )}

                    {activeStep === 1 && (
                      <Stack spacing={4}>
                        <SliderField
                          label="Monte ore settimanale totale su attivita manuali"
                          min={1}
                          max={40}
                          value={state.weeklyHours}
                          unit="h/sett"
                          onChange={(value) => updateState("weeklyHours", value)}
                        />
                        <SliderField
                          label="Costo orario medio lordo azienda"
                          min={15}
                          max={120}
                          value={state.hourlyCost}
                          unit="EUR/h"
                          onChange={(value) => updateState("hourlyCost", value)}
                        />
                        <SliderField
                          label="Errori o rilavorazioni al mese"
                          min={0}
                          max={200}
                          step={5}
                          value={state.monthlyErrors}
                          unit="eventi"
                          onChange={(value) => updateState("monthlyErrors", value)}
                        />
                      </Stack>
                    )}

                    {activeStep === 2 && (
                      <Stack spacing={4}>
                        <RoiInputSummaryChips state={state} />

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <KpiCard
                              label="Risparmio annuo stimato"
                              value={formatMoney(results.totalSavings)}
                              tone="success"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <KpiCard
                              label="Ore recuperate/anno"
                              value={`${formatNumber(results.recoveredHours)} h`}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <KpiCard
                              label="Payback period"
                              value={
                                results.paybackMonths <= 1
                                  ? "< 1 mese"
                                  : `${results.paybackMonths} mesi`
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <KpiCard
                              label="ROI netto"
                              value={`${results.roi >= 0 ? "+" : ""}${results.roi}%`}
                              tone={results.roi >= 0 ? "success" : "danger"}
                            />
                          </Grid>
                        </Grid>

                        <Box
                          sx={{
                            p: 4,
                            borderRadius: 2,
                            backgroundColor: "rgba(84, 189, 193, 0.08)",
                            border: "1px solid rgba(84, 189, 193, 0.22)",
                          }}
                        >
                          <RoiBars
                            timeSavings={results.timeSavings}
                            errorSavings={results.errorSavings}
                            flowentiCost={results.flowentiCost}
                            flowentiPlanName={results.flowentiPlanName}
                            flowentiPlanProcessLimit={results.flowentiPlanProcessLimit}
                          />
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                          Il costo Flowenti e calcolato in base alla subscription necessaria
                          per il numero di processi indicato. I benefici restano benchmark
                          indicativi.
                        </Typography>
                      </Stack>
                    )}

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      gap={2}
                    >
                      <FlowentiButton
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={() => setActiveStep((step) => step - 1)}
                        startIcon={<ArrowBackIcon />}
                        sx={{ py: 1.5 }}
                      >
                        Indietro
                      </FlowentiButton>
                      {activeStep < roiWizardSteps.length - 1 ? (
                        <FlowentiButton
                          variant="contained"
                          onClick={() => setActiveStep((step) => step + 1)}
                          endIcon={activeStep === 1 ? <CalculateIcon /> : <ArrowForwardIcon />}
                          sx={{ py: 1.5 }}
                        >
                          {activeStep === 1 ? "Calcola ROI" : "Avanti"}
                        </FlowentiButton>
                      ) : (
                        <FlowentiButton
                          variant="contained"
                          href="mailto:info@flowenti.com"
                          sx={{ py: 1.5 }}
                        >
                          Richiedi una demo
                        </FlowentiButton>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer lang={lang} />
    </Box>
  );
}
